import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import httpClient from '../services/http-client';
import {
  OrderAttributeFieldDto,
  UpdateOrderAttributeValueRequest,
  UpdateOrderAttributeValueResponse,
  FieldModificationHistoryDto,
  OrderAttributeDto,
  CreateOrderAttributeRequest,
  // Legacy aliases for backward compatibility
  OrderEditableFieldDto,
  UpdateOrderFieldRequest,
  UpdateOrderFieldResponse,
  EditableFieldDto,
  CreateEditableFieldRequest,
} from '../types';

/**
 * Hook to fetch order attributes (editable fields) for a specific order
 */
export function useOrderAttributes(
  workflowId: string,
  orderId: string,
  step: number,
  enabled: boolean = true
) {
  return useQuery<OrderAttributeFieldDto[]>({
    queryKey: ['order-attributes', workflowId, orderId, step],
    queryFn: async () => {
      return await httpClient.get<OrderAttributeFieldDto[]>(
        `/api/v1/workflows/${workflowId}/orders/${orderId}/editable-fields?step=${step}`
      );
    },
    enabled: enabled && !!workflowId && !!orderId,
  });
}

// Legacy alias
export const useOrderEditableFields = useOrderAttributes;

/**
 * Hook to update an order attribute (field) value in an order
 */
export function useUpdateOrderAttributeValue(workflowId: string, orderId: string) {
  const queryClient = useQueryClient();

  return useMutation<UpdateOrderAttributeValueResponse, Error, UpdateOrderAttributeValueRequest>({
    mutationFn: async (request: UpdateOrderAttributeValueRequest) => {
      return await httpClient.patch<UpdateOrderAttributeValueResponse>(
        `/api/v1/workflows/${workflowId}/orders/${orderId}/fields`,
        request
      );
    },
    onSuccess: () => {
      // Invalidate order attributes query
      queryClient.invalidateQueries({ 
        queryKey: ['order-attributes', workflowId, orderId] 
      });
      // Invalidate order detail to refresh the order data
      queryClient.invalidateQueries({ 
        queryKey: ['orders', 'detail'] 
      });
      // Invalidate modification history
      queryClient.invalidateQueries({ 
        queryKey: ['order-field-modifications', workflowId, orderId] 
      });
    },
  });
}

// Legacy alias
export const useUpdateOrderField = useUpdateOrderAttributeValue;

/**
 * Hook to fetch field modification history for an order
 */
export function useOrderFieldModifications(
  workflowId: string,
  orderId: string,
  enabled: boolean = true
) {
  return useQuery<FieldModificationHistoryDto[]>({
    queryKey: ['order-field-modifications', workflowId, orderId],
    queryFn: async () => {
      return await httpClient.get<FieldModificationHistoryDto[]>(
        `/api/v1/workflows/${workflowId}/orders/${orderId}/field-modifications`
      );
    },
    enabled: enabled && !!workflowId && !!orderId,
  });
}

/**
 * Hook to fetch all order attribute configurations for a workflow (Admin)
 */
export function useWorkflowOrderAttributes(
  workflowId: string,
  activeOnly: boolean = true,
  enabled: boolean = true
) {
  return useQuery<OrderAttributeDto[]>({
    queryKey: ['workflow-order-attributes', workflowId, activeOnly],
    queryFn: async () => {
      const params = new URLSearchParams();
      params.append('activeOnly', activeOnly.toString());
      return await httpClient.get<OrderAttributeDto[]>(
        `/api/v1/workflows/${workflowId}/editable-fields?${params.toString()}`
      );
    },
    enabled: enabled && !!workflowId,
  });
}

// Legacy alias
export const useEditableFields = useWorkflowOrderAttributes;

/**
 * Hook to create a new order attribute configuration (Admin)
 */
export function useCreateOrderAttribute(workflowId: string) {
  const queryClient = useQueryClient();

  return useMutation<OrderAttributeDto, Error, CreateOrderAttributeRequest>({
    mutationFn: async (request: CreateOrderAttributeRequest) => {
      return await httpClient.post<OrderAttributeDto>(
        `/api/v1/workflows/${workflowId}/editable-fields`,
        request
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ 
        queryKey: ['workflow-order-attributes', workflowId] 
      });
    },
  });
}

// Legacy alias
export const useCreateEditableField = useCreateOrderAttribute;

/**
 * Hook to update an order attribute configuration (Admin)
 */
export function useUpdateOrderAttribute(workflowId: string, fieldId: string) {
  const queryClient = useQueryClient();

  return useMutation<OrderAttributeDto, Error, Partial<OrderAttributeDto>>({
    mutationFn: async (request: Partial<OrderAttributeDto>) => {
      return await httpClient.put<OrderAttributeDto>(
        `/api/v1/workflows/${workflowId}/editable-fields/${fieldId}`,
        request
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ 
        queryKey: ['workflow-order-attributes', workflowId] 
      });
    },
  });
}

// Legacy alias
export const useUpdateEditableField = useUpdateOrderAttribute;

/**
 * Hook to delete an order attribute configuration (Admin)
 */
export function useDeleteOrderAttribute(workflowId: string) {
  const queryClient = useQueryClient();

  return useMutation<void, Error, string>({
    mutationFn: async (fieldId: string) => {
      await httpClient.delete(
        `/api/v1/workflows/${workflowId}/editable-fields/${fieldId}`
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ 
        queryKey: ['workflow-order-attributes', workflowId] 
      });
    },
  });
}

// Legacy alias
export const useDeleteEditableField = useDeleteOrderAttribute;
